import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Card,
  CrudMode,
  Dialog,
  handleClose,
  Page,
  PageRoute,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { District, DistrictEditResource, DistrictViewResource, Permission } from "generated/openApi";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { DistrictPageBody } from "./DistrictPageBody";
import { DistrictPageHeader } from "./DistrictPageHeader";

const routeName = "district";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
}

export const districtPageRoute: PageRoute<Params> = {
  handle: "District",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
};

export const DistrictPage = () => {
  const ROOT_PERMISSION: Permission = Permission.SystemUtility;
  const navigate = useNavigate();
  const { params } = useParams(routeName, Params);
  const [district, setDistrict] = useState<District>({} as District);
  const [initDistrict, setInitDistrict] = useState<District>(district);
  const [viewResource, setViewResource] = useState<DistrictViewResource>({});
  const [editResource, setEditResource] = useState<DistrictEditResource>({});
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [district] });
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    fetch(params.id);

    ApiService.districts.getViewResource().then((response) => {
      setViewResource(response);
    });
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") {
      ApiService.districts.getEditResource().then((response) => {
        setEditResource(response);
      });
    }
  }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${districtPageRoute.getPath({ id: id, crudMode: crudMode })}`);

  const fetch = (id: number) => {
    ApiService.districts.get(id).then((response) => {
      setDistrict(response);
      setInitDistrict(response);
      resetUnsavedChanges([response]);
    });
  };

  return (
    <Dialog
      isForm
      open={true}
      onClose={() => handleClose(navigate, unsavedChanges)}
      maxWidth="md"
      header={
        <DistrictPageHeader
          rootPermission={ROOT_PERMISSION}
          handle={districtPageRoute.handle}
          crudMode={params.crudMode}
          reroute={reroute}
          fetch={fetch}
          district={district}
          setDistrict={setDistrict}
          initDistrict={initDistrict}
          setValidationErrors={setValidationErrors}
          unsavedChanges={unsavedChanges}
          resetUnsavedChanges={resetUnsavedChanges}
        />
      }
      content={
        <DistrictPageBody
          district={district}
          setDistrict={setDistrict}
          viewResource={viewResource}
          editResource={editResource}
          crudMode={params.crudMode}
          validationErrors={validationErrors}
        />
      }
    />
  );
};
